// =============================================================================
// MobileInventoryReturnSheet.tsx
// Sub-sheet that slides up over the inventory detail sheet. Staff filtered to
// only those currently holding stock (per `outstandingByStaff` map), qty
// clamped to that staff member's holding, optional notes, single "Return"
// submit. Higher z-index than the detail sheet so it stacks cleanly on top.
// =============================================================================

import React, { useMemo, useState } from 'react';

import { inventoryReturn } from '../../lib/api';

import { S } from './mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────

interface Props {
  item: any;
  // Map of staffName -> quantity currently outstanding (ISSUE minus RETURN) for this item.
  outstandingByStaff: Record<string, number>;
  onClose: () => void;
  onDone: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const SUB_SHEET_Z = 400;

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileInventoryReturnSheet({
  item, outstandingByStaff, onClose, onDone, showToast,
}: Props) {
  const [query, setQuery] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  // Only staff with non-zero outstanding are returnable.
  const eligibleNames = useMemo(
    () => Object.keys(outstandingByStaff).sort((a, b) => a.localeCompare(b)),
    [outstandingByStaff]
  );

  // Filter as the user types. Matches empty query → no list (same pattern as Issue sheet).
  const matches = useMemo(() => {
    if (!query.trim() || selectedStaff) return [];
    const q = query.trim().toLowerCase();
    return eligibleNames.filter(name => name.toLowerCase().includes(q)).slice(0, 8);
  }, [query, eligibleNames, selectedStaff]);

  // Max return qty = that staff member's outstanding-out count for this item.
  const maxForStaff = selectedStaff ? (outstandingByStaff[selectedStaff] ?? 0) : 0;
  const overCap = !!selectedStaff && qty > maxForStaff;
  const atCap = !!selectedStaff && qty >= maxForStaff;

  const pick = (name: string) => {
    setSelectedStaff(name);
    setQuery(name);
    const max = outstandingByStaff[name] ?? 0;
    // Clamp current qty down if the newly selected staff's cap is lower.
    if (qty > max) setQty(Math.max(1, max));
  };

  const clearStaff = () => {
    setSelectedStaff(null);
    setQuery('');
    setQty(1);
  };

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 350);
  };

  const canSubmit = !!selectedStaff && qty >= 1 && qty <= maxForStaff && !busy;

  const submit = async () => {
    if (!canSubmit || !selectedStaff) return;
    setBusy(true);
    const noteLine = notes.trim()
      ? `Returned by: ${selectedStaff} — ${notes.trim()}`
      : `Returned by: ${selectedStaff}`;
    const result = await inventoryReturn(item.id, qty, noteLine);
    if (result.ok) {
      showToast(`Returned ${qty} from ${selectedStaff}`, 'success');
      setBusy(false);
      setClosing(true);
      setTimeout(() => { onDone(); }, 350);
    } else {
      setBusy(false);
      showToast(result.message || result.error || 'Return failed', 'error');
    }
  };

  return (
    <div
      style={{ ...S.sheet, zIndex: SUB_SHEET_Z }}
      className={closing ? 'm-sheet-closing' : 'm-sheet-backdrop'}
      onClick={handleClose}
    >
      <div
        style={{ ...S.sheetContent, maxHeight: '85vh' }}
        className="m-sheet-content"
        onClick={e => e.stopPropagation()}
      >
        <div style={S.handle as any} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '4px 20px 12px', gap: 12, borderBottom: '0.5px solid var(--border)',
        }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', flex: 1, wordBreak: 'break-word' }}>
            Return {item.name}
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              width: 44, height: 44, minWidth: 44, minHeight: 44,
              background: 'var(--surface2)', border: 'none', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 20 20" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--text2)', strokeWidth: 2, strokeLinecap: 'round' }}>
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: '14px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Staff field */}
          <div>
            <label style={S.label}>Return from</label>
            <div style={{ position: 'relative' }}>
              <input
                value={query}
                onChange={e => {
                  setQuery(e.target.value);
                  if (selectedStaff && e.target.value !== selectedStaff) setSelectedStaff(null);
                }}
                placeholder={eligibleNames.length === 0 ? 'Nothing currently out' : 'Type staff name…'}
                disabled={eligibleNames.length === 0}
                style={{ ...S.input, paddingRight: selectedStaff ? 40 : 12, opacity: eligibleNames.length === 0 ? 0.6 : 1 }}
              />
              {selectedStaff && (
                <button
                  onClick={clearStaff}
                  aria-label="Clear"
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    width: 32, height: 32, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <svg viewBox="0 0 20 20" style={{ width: 12, height: 12, fill: 'none', stroke: 'var(--text2)', strokeWidth: 2 }}>
                    <path d="M4 4l12 12M16 4L4 16" />
                  </svg>
                </button>
              )}
            </div>

            {matches.length > 0 && (
              <div style={{
                marginTop: 6, background: 'var(--surface2)', borderRadius: 10,
                border: '1px solid var(--border)', maxHeight: 260, overflowY: 'auto',
              }}>
                {matches.map(name => (
                  <button
                    key={name}
                    onClick={() => pick(name)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', minHeight: 44, textAlign: 'left',
                      padding: '10px 12px', fontSize: 'var(--m-text-meta)', color: 'var(--text)',
                      background: 'none', border: 'none', borderBottom: '0.5px solid var(--border)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <span>{name}</span>
                    <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>
                      {outstandingByStaff[name]} out
                    </span>
                  </button>
                ))}
              </div>
            )}

            {selectedStaff && (
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text2)', marginTop: 6 }}>
                {maxForStaff} currently out with {selectedStaff}
              </div>
            )}
          </div>

          {/* Quantity stepper */}
          <div>
            <label style={S.label}>Quantity</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                disabled={!selectedStaff || qty <= 1}
                style={{
                  width: 44, height: 44, background: 'var(--surface2)', border: '1px solid var(--border)',
                  borderRadius: 10, fontSize: 20, color: 'var(--text)',
                  cursor: (selectedStaff && qty > 1) ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                  opacity: (selectedStaff && qty > 1) ? 1 : 0.5,
                }}
              >−</button>
              <input
                type="number"
                inputMode="numeric"
                value={qty}
                min={1}
                disabled={!selectedStaff}
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  if (Number.isNaN(n)) setQty(1);
                  else setQty(Math.max(1, n));
                }}
                style={{ ...S.input, textAlign: 'center', flex: 1, fontWeight: 600, fontSize: 18, opacity: selectedStaff ? 1 : 0.6 }}
              />
              <button
                onClick={() => {
                  if (atCap) return;
                  setQty(Math.min(Math.max(1, maxForStaff), qty + 1));
                }}
                disabled={!selectedStaff || atCap}
                style={{
                  width: 44, height: 44, background: 'var(--surface2)', border: '1px solid var(--border)',
                  borderRadius: 10, fontSize: 20, color: 'var(--text)',
                  cursor: (!selectedStaff || atCap) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  opacity: (!selectedStaff || atCap) ? 0.5 : 1,
                }}
              >+</button>
            </div>
            {overCap && (
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--red)', marginTop: 4 }}>
                Cannot return more than {selectedStaff} has out ({maxForStaff})
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label style={S.label}>Notes (optional)</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes…"
              style={S.input}
            />
          </div>

          {/* Submit */}
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              ...S.greenBtn,
              marginTop: 4,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Returning…' : 'Return'}
          </button>
        </div>
      </div>
    </div>
  );
}
